import React from 'react';
import { Shield, Home, HandCoins, HeartHandshake, BarChart3 } from 'lucide-react';

const deliverables = [
  {
    icon: Home,
    label: 'Protection',
    blurb: 'A funded route home — deceased repatriation, involuntary return, and a voluntary return endowment.',
  },
  {
    icon: HandCoins,
    label: 'Capacity',
    blurb: 'A ring-fenced fee to the mission for welfare and repatriation capability.',
  },
  {
    icon: HeartHandshake,
    label: 'Stewardship',
    blurb: 'Continuous connection so the mission can support citizens who need help with stability and papers.',
  },
  {
    icon: BarChart3,
    label: 'Measurement',
    blurb: 'Voluntary, mission-owned statistical aggregates published under the Consular Data Compact.',
  },
];

const Flagship = () => {
  return (
    <section
      id="flagship"
      className="relative py-16 md:py-24 lg:py-32 bg-[#f5f5f5]"
      data-testid="flagship-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section eyebrow */}
        <div className="mb-10 md:mb-14 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-3">
            Flagship Programme
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2d2d2d] leading-[1.1] mb-4">
            The CRI <span className="text-[#3eb5a0]">Programme</span>
          </h2>
          <p className="text-base md:text-lg text-[#2d2d2d]/60 font-light">
            Consular Remote Identity — a sovereign register for citizens abroad. Supersedes the earlier PVN Programme concept under Pillar 01.
          </p>
        </div>

        {/* Feature card */}
        <article className="relative overflow-hidden rounded-2xl bg-white border border-[#3eb5a0]/20 p-6 md:p-10 lg:p-14 shadow-sm">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 lg:gap-14 items-start">
            {/* Icon block */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#3eb5a0]/10 border border-[#3eb5a0]/30 flex items-center justify-center">
                <Shield size={32} className="text-[#3eb5a0]" strokeWidth={1.6} />
              </div>
            </div>

            {/* Copy */}
            <div className="max-w-3xl">
              <p
                className="text-lg md:text-xl text-[#2d2d2d]/85 font-light mb-5 md:mb-6"
                style={{ lineHeight: '1.75' }}
              >
                <span className="font-medium text-[#2d2d2d]">CRI (Consular Remote Identity)</span> is a
                sovereign identity register that an African state operates for its own citizens
                living abroad. Issued by the home state (the mission), carried by the citizen, and
                funded by the citizen.
              </p>

              <p
                className="text-base md:text-lg text-[#2d2d2d]/70 font-light mb-5 md:mb-6"
                style={{ lineHeight: '1.8' }}
              >
                It is grounded in the Vienna Convention authority to register a state&apos;s own
                nationals anywhere in the world. It is not a host-state immigration system, not a
                commercial digital ID, and not a domestic national ID — it is a consular instrument.
              </p>

              <p
                className="text-base md:text-lg text-[#2d2d2d]/70 font-light mb-8 md:mb-10"
                style={{ lineHeight: '1.8' }}
              >
                Missions often lack a reliable, current register of their nationals abroad. CRI
                delivers a <span className="font-medium text-[#2d2d2d]">living register with a
                funded route home</span>, and <span className="font-medium text-[#2d2d2d]">mission-owned
                real numbers</span> rather than invented ones.
              </p>

              {/* What it delivers */}
              <div className="mb-8 md:mb-10">
                <p className="text-xs uppercase tracking-[0.25em] text-[#3eb5a0] font-semibold mb-4">
                  What it delivers
                </p>
                <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
                  {deliverables.map((d) => {
                    const Icon = d.icon;
                    return (
                      <div
                        key={d.label}
                        className="flex gap-3 md:gap-4 items-start rounded-xl bg-[#f5f5f5] border border-[#3eb5a0]/15 p-4 md:p-5"
                      >
                        <div className="w-10 h-10 rounded-lg bg-white border border-[#3eb5a0]/30 flex items-center justify-center flex-shrink-0">
                          <Icon size={18} className="text-[#3eb5a0]" strokeWidth={2} />
                        </div>
                        <div>
                          <p className="text-sm md:text-base font-semibold text-[#2d2d2d] mb-1">{d.label}</p>
                          <p className="text-xs md:text-sm text-[#2d2d2d]/70 font-light" style={{ lineHeight: '1.65' }}>
                            {d.blurb}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* One-liner */}
              <div className="border-l-2 border-[#3eb5a0] pl-4 md:pl-5 mb-6 md:mb-8 max-w-2xl">
                <p className="text-base md:text-lg text-[#2d2d2d]/85 font-light italic" style={{ lineHeight: '1.75' }}>
                  &ldquo;CRI gives a home state a living register of its citizens abroad, a funded
                  way home, continuous consular connection, and real numbers — issued by the
                  country, carried by the citizen, counted with dignity.&rdquo;
                </p>
              </div>

              {/* Placeholder for future application link */}
              <div className="border-l-2 border-[#3eb5a0]/40 pl-4 md:pl-5 max-w-2xl">
                <p className="text-sm md:text-base text-[#2d2d2d]/55 font-light italic" style={{ lineHeight: '1.7' }}>
                  Application link to follow.
                </p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default Flagship;
